import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "../../lib/utils";
import { HeroDotField } from './HeroDotField';
import {
  Send,
  Menu,
  MessageSquare,
  Sparkles,
  Plus,
  Presentation,
  Video,
  Upload,
  X,
  Sun,
  Moon,
  Users,
  Copy,
  Eye,
  Loader2,
  Settings,
  Palette,
} from "lucide-react";
import { toast } from "../../hooks/use-toast";
import Logo from '../ui/logo';
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { VideoProjectCard } from "./VideoProjectCard";
import { HeroResourceUploadArea, StagedFile, UploadState } from "./HeroResourceUploadArea";
import { TemplateSelectionModal, ColorPalette } from "./TemplateSelectionModal";
import DESIGN_PACKS_CATALOG from '@/generated/design-packs-catalog.json';
import DESIGN_PACKS_EDU_CATALOG from '@/generated/design-packs-edu-catalog.json';
import { cardTheme, packLabel, type StyleCardLike } from '@/lib/designPackAccess';
import { lookName, videoLook } from '@/lib/studioB/takeover';
import { AIImageGenerationModal } from "./AIImageGenerationModal";
import { DEFAULT_MODEL } from "../../types/models";
import { useQualityTier } from "../../hooks/useQualityTier";
import { TierPicker } from "./TierPicker";
import api, { conversationAPI, templateAPI, slidesAPI, scriptAudioAPI, projectShareAPI, type ProjectShareInfo } from "../../services/api";
import { ShareProjectDialog } from "./ShareProjectDialog";
import { useConversationStore } from "../../stores/conversationStore";
import { useConversationsCount, STORE_PAGE_LIMIT } from "../../hooks/useConversations";
import { useUserStore } from "../../stores/userStore";
import { Link, useNavigate } from "@tanstack/react-router";
import { projectInitializationService } from "../../services/projectInitializationService";
import { conversationUpdatesService, type TranscriptionEvent } from "../../services/conversationUpdatesService";
import { templateProjectInitializationService } from "../../services/templateProjectInitializationService";
import type { ProviderType } from "../../services/messageService";
import { useTheme } from "../../hooks/useTheme";
import { useIvyChatAuth } from "../../hooks/useIvyChatAuth";
import { useKubicleAllowed } from "../../hooks/useKubicleAllowed";
import { useKubicle1aStart } from "../../hooks/useKubicle1aStart";
import * as ivyChatApi from "../../services/ivyChatApi";
import { ClaudeTokenModal } from "../agent-chat/ClaudeTokenModal";
import { FootageChips } from "./FootageChips";
import { AttachFilesButton, ComposerDropZone } from "./ComposerAttach";
import { startAndHoldMessage, useFootageIntakeStore, uploadingFilesContext } from "@/stores/footageIntakeStore";
import { extractFootageLinks } from "@/lib/footage/links";
import { getFootageUploadClient, needsResumableUpload } from "@/lib/footage/uploadClient";
import { useFrontDoorResume } from "@/hooks/useFrontDoorResume";
import { claimFrontDoor } from "@/lib/frontDoor/capture";
import { releaseCover } from "@/lib/frontDoor/cover";
import { handOffFrom, type FrontDoorStart } from "@/lib/frontDoor/handoff";
import type { Chip } from "@/lib/frontDoor/chips";
import { studioBOn } from "@/lib/studioB/flag";
import { IconTipB } from "@/components/studio/IconTipB";
import { IvyMark } from "@/components/studio/IvyMark";
import { HomeIdeasB } from "./HomeIdeasB";
import { videoStage } from "@/lib/studioB/videoStage";
import { projectName } from "@/lib/projectName";
import { ResumeBanner } from "../FrontDoor/ResumeBanner";

const LOOK_CARDS = [...DESIGN_PACKS_CATALOG, ...DESIGN_PACKS_EDU_CATALOG] as unknown as StyleCardLike[];

interface VideoProject {
  id: string;
  title: string;
  thumbnail?: string;
  slidePreviewUrls?: string[]; // For VIDEO projects: up to 4 slide preview URLs for mosaic display
  duration?: string;
  status: "draft" | "processing" | "completed" | "failed";
  lastModified: Date;
  createdAt: Date;
  fileSize?: number;
  tags: string[];
  description?: string;
  projectType?: 'video' | 'slide';
  hasOutlineTitle?: boolean; // True if title is from generated outline (show full title with smaller font)
  stage?: string | null;
}

interface HeroScreenProps {
  isTransitioning: boolean;
  hasActiveTeam: boolean;
  onToggleSidebar?: () => void;
  showLogo?: boolean;
}

export function HeroScreen({
  isTransitioning,
  hasActiveTeam,
  onToggleSidebar,
  showLogo = true,
}: HeroScreenProps) {
  // Feature flags
  const isTemplateWorkflowEnabled = import.meta.env.VITE_ENABLE_TEMPLATE_WORKFLOW === 'true';
  const isSlideWorkflowEnabled = import.meta.env.VITE_ENABLE_SLIDE_WORKFLOW === 'true';

  const [message, setMessage] = useState("");
  const [selectedModel] = useState(DEFAULT_MODEL);
  const [selectedProvider] = useState<ProviderType>('claude');
  const [qualityTier, setQualityTier] = useQualityTier();
  const [workflowType, setWorkflowType] = useState<'video' | 'template' | 'slide' | 'image'>('video');

  // Kubicle accounts (the effective viewer, so admin impersonation sees theirs) get the Kubicle 1A button.
  const kubicleAudioAllowed = useKubicleAllowed();

  // Kubicle 1A (Kubicle accounts): the box holds the lesson script, narrated word for word, and the lesson is
  // created on API v2 so it carries the workflow's locks and the staging library it pins.
  const [kubicle1aMode, setKubicle1aMode] = useState(false);
  const [lessonTitle, setLessonTitle] = useState('');
  const kubicle1a = useKubicle1aStart();

  // Reference image state (for "From Image" workflow)
  const [referenceImage, setReferenceImage] = useState<File | null>(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [videoProjects, setVideoProjects] = useState<VideoProject[]>([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectFilter, setProjectFilter] = useState<'all' | 'slide' | 'video' | 'shared'>('video');

  // Sharing state
  const [shareDialogProject, setShareDialogProject] = useState<{ id: string; title: string } | null>(null);
  const [sharedProjects, setSharedProjects] = useState<ProjectShareInfo[]>([]);
  const [loadingShared, setLoadingShared] = useState(false);
  const [cloningShareId, setCloningShareId] = useState<string | null>(null);

  // Inline upload state (replaces modal)
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);
  const [uploadState, setUploadState] = useState<UploadState>('empty');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [analysisJobId, setAnalysisJobId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);
  const [uploadConversationId, setUploadConversationId] = useState<string | undefined>(undefined);

  useEffect(() => useFootageIntakeStore.getState().pruneFinished(), []);
  useEffect(() => useFootageIntakeStore.getState().syncLinks(extractFootageLinks(message)), [message]);
  // Template + design system selection
  const [selectedTemplateId] = useState<string | null>('blank-canvas');
  // "Another in this look" brings the video's own look as it was, so it sends no card theme.
  const [selectedDesignPackId, setSelectedDesignPackId] = useState<string | undefined>(() => new URLSearchParams(window.location.search).get('look') ?? undefined);
  const [lookAsItWas, setLookAsItWas] = useState(() => new URLSearchParams(window.location.search).has('look'));
  const [selectedColorPalette, setSelectedColorPalette] = useState<ColorPalette | undefined>(undefined);
  const lookLabel = selectedDesignPackId ? packLabel(LOOK_CARDS, selectedDesignPackId) ?? lookName(selectedDesignPackId) : 'Ivy picks';
  const [showDesignSystemModal, setShowDesignSystemModal] = useState(false);

  // AI Image Generation modal state
  const [showImageGenModal, setShowImageGenModal] = useState(false);

  const { conversations, addConversation, updateConversationProject, deleteConversation, isLoading: conversationsLoading } = useConversationStore();
  const { activeOrganization, activeTeam, termsAccepted } = useUserStore();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  // landing: the draft came from the landing page (its row is claimed); else an idea picked on this screen.
  const frontDoorStart = useRef<(FrontDoorStart & { prompt: string; landing: boolean; noticed?: boolean }) | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Filtered project total: when the store holds the full list (not capped at the
  // server page limit), its filtered length is authoritative and updates on
  // add/delete with no fetch. Only when capped do we hit the server count query
  // (shared key with the Videos page, so the two pages dedupe to one call).
  const storeCapped = conversations.length >= STORE_PAGE_LIMIT;
  const collectionFilter = projectFilter === 'video' ? ['VIDEO']
    : projectFilter === 'slide' ? ['SLIDE']
    : ['VIDEO', 'SLIDE'];
  const { data: serverCount } = useConversationsCount(
    activeTeam?.id ?? null,
    collectionFilter,
    storeCapped,
  );
  const totalProjectCount = storeCapped ? (serverCount ?? filteredCount) : filteredCount;

  // Claude token settings
  const { authReady: ivyChatAuthReady } = useIvyChatAuth();
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<{ hasToken: boolean; hint: string | null } | null>(null);
  const [platformFunded, setPlatformFunded] = useState(false);

  const checkTokenStatus = useCallback(async () => {
    let funded = false;
    try {
      funded = await ivyChatApi.isPlatformFunded();
      setPlatformFunded(funded);
      const providerStatus = await ivyChatApi.getProviderStatus();
      const activeP = providerStatus.providers[providerStatus.activeProvider];
      setTokenStatus({
        hasToken: providerStatus.hasAnyKey,
        hint: activeP?.hint || null,
      });
      return { funded, hasToken: providerStatus.hasAnyKey };
    } catch {
      return { funded, hasToken: false };
    }
  }, []);

  useEffect(() => {
    if (ivyChatAuthReady) checkTokenStatus();
  }, [ivyChatAuthReady, checkTokenStatus]);

  // Force workflow type to 'slide' when specific workflows are disabled
  useEffect(() => {
    if (!isTemplateWorkflowEnabled && workflowType === 'template') {
      setWorkflowType('slide');
    }
    if (!isSlideWorkflowEnabled && workflowType === 'slide') {
      setWorkflowType('video'); // Fallback to video if slide is also disabled
    }
  }, [isTemplateWorkflowEnabled, isSlideWorkflowEnabled, workflowType]);

  // Load video and slide projects
  useEffect(() => {
    const fetchVideoProjects = () => {
      try {
        setLoadingProjects(true);

        // Filter video and slide conversations and transform them to projects
        const videoConversations = conversations.filter(
          (c) => c.collectionType === "VIDEO",
        );
        const slideConversations = conversations.filter(
          (c) => c.collectionType === "SLIDE",
        );

        const videoProjectsList: VideoProject[] = videoConversations.map((conv) => {
          // Get title from planJson (outline) if available, otherwise fallback to metadata or conv.title
          const planJson = conv.planJson as { title?: string; lookRef?: string | null; lookLabel?: string | null } | null;
          const outlineTitle = planJson?.title;
          const hasOutlineTitle = !!outlineTitle;

          return {
            id: conv.id,
            title: projectName(conv, outlineTitle),
            hasOutlineTitle, // Flag to show full title with smaller font
            // Published poster (set_video_thumbnail) wins; slide 1 preview is the fallback
            thumbnail: conv.thumbnailUrl || conv.slideThumbnailUrl || conv.metadata?.projectThumbnail,
            slidePreviewUrls: conv.slidePreviewUrls || undefined, // Keep for potential future use
            status: "completed" as const,
            stage: videoStage(conv),
            ...videoLook(planJson),
            lastModified: new Date(conv.lastActivity),
            createdAt: new Date(conv.createdAt),
            tags: [],
            projectType: 'video' as const,
          };
        });

        const slideProjects: VideoProject[] = slideConversations.map((conv) => ({
          id: conv.id,
          title: projectName(conv),
          // Priority: poster (set_video_thumbnail) > slideThumbnailUrl (actual slide preview) > projectThumbnail (from state.json)
          thumbnail: conv.thumbnailUrl || conv.slideThumbnailUrl || conv.metadata?.projectThumbnail,
          status: "completed" as const,
          lastModified: new Date(conv.lastActivity),
          createdAt: new Date(conv.createdAt),
          tags: [],
          projectType: 'slide' as const,
        }));

        // Combine all projects
        const allProjects = [...videoProjectsList, ...slideProjects];

        // Filter based on selected project filter tab (independent of workflow type)
        let filteredProjects = allProjects;
        if (projectFilter === 'video') {
          filteredProjects = allProjects.filter(p => p.projectType === 'video');
        } else if (projectFilter === 'slide') {
          filteredProjects = allProjects.filter(p => p.projectType === 'slide');
        }
        // 'all' shows everything

        // Sort by last modified, most recent first
        filteredProjects.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime());

        // Show only the 12 most recent on the hero page
        setVideoProjects(filteredProjects.slice(0, 12));
        setFilteredCount(filteredProjects.length);
      } catch (error) {
        console.error("Failed to load projects:", error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchVideoProjects();
  }, [conversations, projectFilter]);

  // Load shared projects on mount so we know whether to show the Shared tab
  // even when the user has no projects of their own.
  // TODO: replace with a lightweight count-only endpoint to avoid fetching
  // every share on every hero load.
  useEffect(() => {
    const loadShared = async () => {
      try {
        setLoadingShared(true);
        const result = await projectShareAPI.sharedWithMe();
        setSharedProjects(result.shares);
      } catch (error) {
        console.error('Failed to load shared projects:', error);
      } finally {
        setLoadingShared(false);
      }
    };
    loadShared();
  }, []);

  // The Videos tab is always the default landing. (Previously an effect
  // auto-picked the tab once data loaded, but it could run before `conversations`
  // hydrated — seeing no videos yet but shared projects present — and lock onto
  // the Shared tab even for users who do have videos.)

  const handleCloneShared = async (share: ProjectShareInfo) => {
    try {
      setCloningShareId(share.id);
      const result = await projectShareAPI.clone(share.id);

      // Pull the new conversation into the local store so it shows up on the
      // hero without a page reload. The clone endpoint only returns the id.
      try {
        const { conversation } = await conversationAPI.getById(result.newProjectId);
        addConversation(conversation);
      } catch (err) {
        console.warn('Failed to hydrate cloned conversation into store:', err);
      }

      toast({
        title: 'Project cloned',
        description: `"${share.projectTitle}" has been added to your projects.`,
      });
      navigate({ to: '/chat/$conversationId/video-workspace', params: { conversationId: result.newProjectId } });
    } catch (error: any) {
      console.error('Failed to clone shared project:', error);
      toast({
        title: 'Clone failed',
        description: error?.response?.data?.error || error?.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCloningShareId(null);
    }
  };

  const handleViewShared = (share: ProjectShareInfo) => {
    navigate({
      to: '/chat/$conversationId/video-workspace',
      params: { conversationId: share.conversationId },
    });
  };

  // Handle project click
  const handleProjectClick = (project: VideoProject) => {
    const isSlideProject = project.projectType === 'slide'

    if (isSlideProject) {
      navigate({
        to: "/chat/$conversationId/slide-workspace",
        params: { conversationId: project.id },
        search: { tab: undefined },
      });
    } else {
      navigate({
        to: "/chat/$conversationId/video-workspace",
        params: { conversationId: project.id },
      });
    }
  };

  // Handle project delete
  const handleProjectDelete = async (project: VideoProject) => {
    try {
      await conversationAPI.delete(project.id);
      // Update the store to remove the conversation
      deleteConversation(project.id);
      toast({
        title: "Project deleted",
        description: `"${project.title}" has been deleted.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast({
        title: "Delete failed",
        description: "Could not delete the project. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      throw error; // Re-throw to let the card know it failed
    }
  };

  // Handle project clone — creates a copy and navigates to the new project
  const handleProjectClone = async (project: VideoProject) => {
    try {
      const result = await conversationAPI.clone(project.id, {
        newTitle: `Copy of ${project.title}`,
      });

      // Pull the new conversation into the local store so it shows up on the
      // hero without a page reload. The clone endpoint only returns the id.
      try {
        const { conversation } = await conversationAPI.getById(result.newProjectId);
        addConversation(conversation);
      } catch (err) {
        console.warn('Failed to hydrate cloned conversation into store:', err);
      }

      toast({
        title: "Project cloned",
        description: `${result.slidesCopied} slides copied. Opening the new project…`,
        duration: 3000,
      });
      navigate({
        to: "/chat/$conversationId/video-workspace",
        params: { conversationId: result.newProjectId },
      });
    } catch (error) {
      console.error('Failed to clone project:', error);
      toast({
        title: "Clone failed",
        description: "Could not clone the project. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      throw error;
    }
  };

  // Helper: Classify file into category
  const classifyFile = (fileName: string): 'images' | 'documents' | 'others' => {
    const ext = fileName.split('.').pop()?.toLowerCase();

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
      return 'images';
    }

    if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext || '')) {
      return 'documents';
    }

    return 'others';
  };

  // Callback: When files are added via drag & drop or browse
  const handleFilesAdded = (added: File[]) => {
    const footage = getFootageUploadClient() ? added.filter(needsResumableUpload) : [];
    if (footage.length) useFootageIntakeStore.getState().addFiles(footage);
    const files = added.filter(f => !footage.includes(f));
    if (!files.length) return;
    const newStagedFiles: StagedFile[] = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      category: classifyFile(file.name),
      purpose: 'READ_UNDERSTAND', // Default purpose
    }));

    setStagedFiles(prev => [...prev, ...newStagedFiles]);
    setUploadState('files_staged');
  };

  // Callback: When file is removed
  const handleFileRemoved = (fileId: string) => {
    setStagedFiles(prev => {
      const updated = prev.filter(f => f.id !== fileId);
      if (updated.length === 0) {
        setUploadState('empty');
      }
      return updated;
    });
  };

  // Callback: When file purpose is toggled
  const handlePurposeChanged = (fileId: string, purpose: 'READ_UNDERSTAND' | 'ADD_TO_SCENES') => {
    setStagedFiles(prev =>
      prev.map(f => (f.id === fileId ? { ...f, purpose } : f))
    );
  };

  // Handle reference image selection (for "From Image" workflow)
  const handleReferenceImageSelect = (file: File) => {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Please upload a PNG, JPG, or WebP image');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Image must be smaller than 10MB');
      return;
    }

    setReferenceImage(file);
    setUploadError(undefined);

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setReferenceImagePreview(previewUrl);
  };

  // Handle reference image removal
  const handleReferenceImageRemove = () => {
    if (referenceImagePreview) {
      URL.revokeObjectURL(referenceImagePreview);
    }
    setReferenceImage(null);
    setReferenceImagePreview(null);
  };

  // Handle drag and drop for reference image
  const handleImageDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleReferenceImageSelect(files[0]);
    }
  };

  const handleImageDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle AI-generated image approval
  const handleGeneratedImageApproved = async (imageUrl: string) => {
    try {
      // Download the generated image as blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a File object from the blob
      const file = new File([blob], `ai-generated-${Date.now()}.png`, {
        type: 'image/png',
      });

      // Use existing reference image handler
      handleReferenceImageSelect(file);

      // Close the modal
      setShowImageGenModal(false);

      toast({
        title: 'Image ready',
        description: 'AI-generated image selected. You can now create your slide.',
      });
    } catch (error) {
      console.error('Failed to download generated image:', error);
      toast({
        title: 'Error',
        description: 'Failed to use the generated image. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Callback: When analysis completes
  const handleAnalysisComplete = async () => {
    console.log('✅ Analysis complete, navigating to chat');

    try {
      // Navigate to chat - workflow guide will show "Create Plan" button
      // User will manually click to start planning
      if (!uploadConversationId) {
        console.error('❌ No conversation ID found');
        setUploadError('No conversation ID found');
        setUploadState('error');
        return;
      }

      const footageClient = getFootageUploadClient();
      if (footageClient) void useFootageIntakeStore.getState().start(uploadConversationId, footageClient);

      await navigate({
        to: "/chat/$conversationId/video-workspace",
        params: { conversationId: uploadConversationId },
      });

      console.log('✅ Navigation complete - ready for user to start planning');

      // Clear state
      setStagedFiles([]);
      setUploadConversationId(undefined);
      setUploadState('empty');
      setAnalysisJobId(null);
      setMessage('');
    } catch (error) {
      console.error('❌ Error navigating after analysis:', error);
      setUploadError('Failed to navigate to chat');
      setUploadState('error');
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If files are uploading or analyzing, prevent submit
    if (uploadState === 'uploading' || uploadState === 'analyzing') {
      return;
    }

    // Template workflow requires files (only enforce if template workflow is enabled)
    if (isTemplateWorkflowEnabled && workflowType === 'template' && stagedFiles.length === 0) {
      setUploadError('Template workflow requires files to be uploaded');
      return;
    }

    // Template defaults to blank-canvas, no manual selection required

    // Image workflow requires reference image
    if (workflowType === 'image' && !referenceImage) {
      setUploadError('Please upload an image to continue');
      return;
    }

    // For image workflow, message is optional. For other workflows, message is required.
    if (workflowType !== 'image' && !message.trim()) return;

    // Credential gate: don't start a video with no way to pay for it — open the key
    // configurer instead. Under CREDENTIAL_SOURCE=platform the server leases from the
    // account pool, so no key is required and the gate stands down (PRD B2). A send that
    // beats the status (it loads only after the ivy-chat session) reads it first, so an
    // unknown status never counts as "no key".
    const key = tokenStatus !== null || platformFunded
      ? { funded: platformFunded, hasToken: !!tokenStatus?.hasToken }
      : await checkTokenStatus();
    if (!key.hasToken && !key.funded) {
      setShowTokenModal(true);
      return;
    }
    if (isLoading || !hasActiveTeam) return;

    if (kubicle1aMode) {
      await kubicle1a.start(lessonTitle, message);
      return;
    }

    try {
      let convId = uploadConversationId;

      // Step 1: ALWAYS create conversation and initialize project first
      if (!convId) {
        if (!activeOrganization || !activeTeam) {
          alert('Please select an organization and team first');
          return;
        }

        const isTemplateWorkflow = workflowType === 'template';
        const isSlideWorkflow = workflowType === 'slide';
        const isImageWorkflow = workflowType === 'image';

        const workflowLabel = isTemplateWorkflow ? 'template' : isSlideWorkflow ? 'slide' : isImageWorkflow ? 'image' : 'video';
        console.log(`📝 Creating ${workflowLabel} conversation...`);

        const response = await conversationAPI.create({
          title: message.trim().substring(0, 50) || (isImageWorkflow ? 'Slide from Image' : `${workflowLabel.charAt(0).toUpperCase() + workflowLabel.slice(1)} Project`),
          model: selectedModel,
          organizationId: activeOrganization.id,
          teamId: activeTeam.id,
          styleId: undefined,
          collectionType: isTemplateWorkflow ? 'STYLE' : (isSlideWorkflow || isImageWorkflow) ? 'SLIDE' : 'VIDEO',
        });

        convId = response.conversation.id;
        addConversation(response.conversation);
        setUploadConversationId(convId);

        // Mark project type for workflow guide
        updateConversationProject(convId, {
          hasVideoProject: !isTemplateWorkflow && !isSlideWorkflow && !isImageWorkflow,
          hasStyleProject: isTemplateWorkflow,
          hasSlideProject: isSlideWorkflow || isImageWorkflow,
          projectId: convId,
          workingDirectory: undefined, // Will be set after initialization
        });

        // Initialize project structure (happens for both with/without files)
        if (isTemplateWorkflow) {
          console.log('🎨 Initializing template project structure...');
          await templateProjectInitializationService.initializeProject({
            conversationId: convId,
            workingDirectory: undefined,
            initGit: true,
            retryOnGitError: true
          });
          console.log('✅ Template project initialized successfully');
        } else if (!isSlideWorkflow && !isImageWorkflow) {
          // Only video needs project initialization
          console.log('🎬 Initializing video project structure...');
          await projectInitializationService.initializeProject({
            conversationId: convId,
            workingDirectory: undefined,
            initGit: true,
            retryOnGitError: true
          });
          console.log('✅ Video project initialized successfully');

          // Apply template for VIDEO workflow
          if (selectedTemplateId) {
            console.log('📋 Applying template for video:', selectedTemplateId);
            try {
              const templateResult = await templateAPI.applyTemplate(convId, selectedTemplateId);
              console.log('✅ Template applied for video:', templateResult);
            } catch (templateError) {
              console.error('⚠️ Failed to apply template for video:', templateError);
              // Don't fail the entire flow if template application fails
            }
          }
        } else if (isImageWorkflow && referenceImage) {
          // Image workflow: Upload reference image and create slide
          console.log('🖼️ Creating slide from reference image...');
          setUploadState('uploading');
          try {
            const imageResult = await slidesAPI.createFromImage(
              convId,
              referenceImage,
              message.trim() || undefined
            );
            console.log('✅ Slide created from image:', imageResult);
            setUploadState('empty');
          } catch (imageError) {
            console.error('❌ Failed to create slide from image:', imageError);
            setUploadError(imageError instanceof Error ? imageError.message : 'Failed to create slide from image');
            setUploadState('error');
            setIsLoading(false);
            return;
          }
        } else {
          // Slide projects are initialized by the backend
          console.log('✅ Slide session ready');

          // Apply template if one was selected
          if (isSlideWorkflow && selectedTemplateId) {
            console.log('📋 Applying selected template:', selectedTemplateId);
            try {
              const templateResult = await templateAPI.applyTemplate(convId, selectedTemplateId);
              console.log('✅ Template applied successfully:', templateResult);
            } catch (templateError) {
              console.error('⚠️ Failed to apply template:', templateError);
              // Don't fail the entire flow if template application fails
              // User can apply template later from the Templates tab
            }
          }
        }

        // Update state.json with user instructions (video and slide projects)
        if (!isTemplateWorkflow) {
          const stateFileName = (isSlideWorkflow || isImageWorkflow) ? 'slide-state.json' : 'state.json';
          console.log(`💾 Saving user instructions to ${stateFileName}...`);
          try {
            await api.put(`/project/${convId}/state`, {
              id: convId,
              title: message.trim().substring(0, 50) || (isImageWorkflow ? 'Slide from Image' : `${isSlideWorkflow ? 'Slide' : 'Video'} Project`),
              creation_date: new Date().toISOString(),
              userInstructions: message.trim() || (isImageWorkflow ? 'Recreate this slide from the uploaded image' : ''),
              video_status: 'initialized',
              working_directory: null,
              project_metadata: {
                working_directory: null,
                project_root: null,
                state_file_path: null
              }
            });
            console.log(`✅ ${isSlideWorkflow || isImageWorkflow ? 'Slide' : 'Video'} project instructions saved to ${stateFileName}`);
          } catch (error) {
            console.error('⚠️ Failed to save user instructions:', error);
            // Don't fail the flow if state update fails
          }
        } else {
          console.log('✅ Template project state.json already initialized by server');
        }
      }

      // Step 2: If files are staged, upload and analyze them
      let uploadedResourceContext: string | undefined;
      if (stagedFiles.length > 0 && uploadState === 'files_staged') {
        setUploadState('uploading');
        const formData = new FormData();
        formData.append('conversationId', convId);

        // Add user instructions for context-aware analysis
        if (message.trim()) {
          formData.append('userInstructions', message.trim());
        }

        const purposesArray = stagedFiles.map(f => f.purpose);
        formData.append('purposes', JSON.stringify(purposesArray));

        // Preserve directory structure by sending relative paths
        const filePaths: string[] = [];
        stagedFiles.forEach(stagedFile => {
          formData.append('files', stagedFile.file);
          // Use webkitRelativePath if available (from directory upload), otherwise just filename
          const relativePath = (stagedFile.file as any).webkitRelativePath || stagedFile.file.name;
          filePaths.push(relativePath);
        });
        formData.append('filePaths', JSON.stringify(filePaths));

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setUploadProgress(percentCompleted);
          }
        });

        if (!uploadResponse.data.success) {
          throw new Error(uploadResponse.data.error || 'Upload failed');
        }

        // Build attachment context so ivy-chat knows about the uploaded files
        const successfulUploads = (uploadResponse.data.results || []).filter(
          (r: any) => r.success && r.resourceId
        );
        if (successfulUploads.length > 0) {
          const ctx = successfulUploads
            .map((r: any) => `${r.originalName} (id:${r.resourceId.slice(0, 8)})`)
            .join(', ');
          uploadedResourceContext = `[attachments: ${ctx}]`;
        }

        // Auto-trigger transcription for any uploaded audio files and WAIT for completion.
        const audioResults = (uploadResponse.data.results || []).filter(
          (r: any) => r.success && r.resourceId && r.mimeType?.startsWith('audio/')
        );
        if (audioResults.length > 0) {
          setUploadState('transcribing');

          // Queue all transcription jobs
          const queuedResourceIds = new Set<string>();
          for (const audioResult of audioResults) {
            try {
              await scriptAudioAPI.transcribeAudio(convId, audioResult.resourceId);
              queuedResourceIds.add(audioResult.resourceId);
              console.log(`[HeroScreen] Queued transcription for ${audioResult.originalName}`);
            } catch (err) {
              console.warn(`[HeroScreen] Failed to queue transcription for ${audioResult.originalName}:`, err);
            }
          }

          // Wait for all transcriptions to complete via SSE events
          if (queuedResourceIds.size > 0) {
            const pendingIds = new Set(queuedResourceIds);
            await new Promise<void>((resolve) => {
              const timeout = setTimeout(() => {
                unsubscribe();
                console.warn(`[HeroScreen] Transcription timed out after 3 minutes, continuing without transcript`);
                resolve();
              }, 180000); // 3 minute timeout

              const unsubscribe = conversationUpdatesService.onTranscriptionEvent(
                (_eventConvId: string, event: TranscriptionEvent) => {
                  if (_eventConvId !== convId) return;

                  if (event.type === 'transcription_complete' && pendingIds.has(event.resourceId)) {
                    pendingIds.delete(event.resourceId);
                    console.log(`[HeroScreen] Transcription complete for ${event.resourceId}: ${event.wordCount} words, ${event.duration?.toFixed(1)}s`);
                    if (pendingIds.size === 0) {
                      clearTimeout(timeout);
                      unsubscribe();
                      resolve();
                    }
                  } else if (event.type === 'transcription_error' && pendingIds.has(event.resourceId)) {
                    pendingIds.delete(event.resourceId);
                    console.warn(`[HeroScreen] Transcription failed for ${event.resourceId}:`, event.error);
                    if (pendingIds.size === 0) {
                      clearTimeout(timeout);
                      unsubscribe();
                      resolve(); // Continue without transcript
                    }
                  }
                }
              );
            });
          }

          setUploadState('empty');
        }

        // If analysis job started, wait for it to complete
        if (uploadResponse.data.analysisJobId) {
          setAnalysisJobId(uploadResponse.data.analysisJobId);
          setUploadState('analyzing');
          // Analysis component will handle completion via handleAnalysisComplete callback
          // which will send the message and navigate to chat
          return; // Don't send message now, wait for analysis
        } else {
          // If no analysis, fall through to send message below
          setUploadState('empty');
        }
      }
      // Video workflow without files: proceed directly to workspace
      // The workspace will handle sending the initial message with outline context

      // Step 3: Navigate to chat (workflow guide will show "Create Plan" button)
      setIsLoading(true);

      // Build the message to send — include attachment context if files were uploaded
      let messageToSend = message;
      if (uploadedResourceContext) {
        messageToSend = messageToSend
          ? `${messageToSend}\n\n${uploadedResourceContext}`
          : uploadedResourceContext;
      }

      // Footage uploads/imports start now and keep running after navigation (the store owns them).
      // A PDF or PSD lands first, so it's there when the agent's turn starts.
      const footageClient = getFootageUploadClient();
      const footageTag = footageClient ? uploadingFilesContext(useFootageIntakeStore.getState().items) : null;
      if (footageClient && convId) {
        setUploadState('empty');
        await startAndHoldMessage(convId, footageClient);
      }
      if (footageTag) messageToSend = messageToSend ? `${messageToSend}\n\n${footageTag}` : footageTag;

      // Include the selected design pack so the agent knows it.
      if (selectedDesignPackId) {
        const theme = lookAsItWas ? undefined : cardTheme(LOOK_CARDS, selectedDesignPackId);
        const packTag = `[design-pack: ${selectedDesignPackId}]${theme ? `\n[look-theme: ${theme}]` : ''}`;
        messageToSend = messageToSend
          ? `${messageToSend}\n\n${packTag}`
          : packTag;
      }

      // Include selected color palette with semantic roles so slides use these colors
      if (selectedColorPalette) {
        const r = selectedColorPalette.roles;
        const paletteTag = `[color-palette: ${selectedColorPalette.name} | primary=${r.primary}, secondary=${r.secondary}, accent=${r.accent}, background=${r.background}, text=${r.text}]`;
        messageToSend = messageToSend
          ? `${messageToSend}\n${paletteTag}`
          : paletteTag;
      }

      console.log('🚀 Navigating to chat with conversation:', convId);

      // Navigate to slide-workspace for slide/image conversations, gallery for others
      const isSlideOrImageWorkflow = workflowType === 'slide' || workflowType === 'image';
      if (isSlideOrImageWorkflow) {
        await navigate({
          to: "/chat/$conversationId/slide-workspace",
          params: { conversationId: convId },
          search: { tab: undefined },
          state: {
            autoSend: true,
            userInstructions: (messageToSend || (workflowType === 'image' ? 'Recreate this slide from the uploaded image' : '')),
            provider: selectedProvider,
            sourceType: workflowType === 'image' ? 'image' : 'template',
          } as any,
        });
      } else {
        if (frontDoorStart.current?.landing && frontDoorStart.current.prompt === message) {
          if (frontDoorStart.current.noticed) void claimFrontDoor(message, convId);
        }
        // Direction B: the words fly from this box into the plan view, as they do from the landing page into /start.
        else if (studioBOn() && boxRef.current) {
          const r = boxRef.current.getBoundingClientRect();
          handOffFrom({ left: r.left, top: r.top, width: r.width, height: r.height });
        }
        await navigate({
          to: "/chat/$conversationId/video-workspace",
          params: { conversationId: convId },
          state: {
            autoSend: true,
            userInstructions: messageToSend,
            provider: selectedProvider,
            frontDoor: frontDoorStart.current?.prompt === message
              ? { chip: frontDoorStart.current.chip, look: frontDoorStart.current.look }
              : undefined,
          } as any,
        });
      }

      console.log('✅ Navigation complete - workflow guide ready for user to start planning');

      // Clear state
      setStagedFiles([]);
      setUploadConversationId(undefined);
      setUploadState('empty');
      setAnalysisJobId(null);
      setIsLoading(false);

      // Clear reference image state (for image workflow)
      if (referenceImagePreview) {
        URL.revokeObjectURL(referenceImagePreview);
      }
      setReferenceImage(null);
      setReferenceImagePreview(null);

    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to send message');
      setUploadState('error');
      setIsLoading(false);
    }
  };

  const resume = useFrontDoorResume({
    ready: hasActiveTeam && !!activeOrganization && !!activeTeam && (tokenStatus !== null || platformFunded) && termsAccepted === true,
    paused: termsAccepted === false,
    fill: (draft) => {
      frontDoorStart.current = { prompt: draft.prompt, chip: draft.chip, look: draft.look, landing: true, noticed: draft.noticed };
      setMessage(draft.prompt);
      // A look the words name is a lean: the words go along and Ivy still offers three. Only a pick locks the look.
      if (draft.look?.id && draft.look.from === 'picked') setSelectedDesignPackId(draft.look.id);
    },
    start: () => handleSubmit({ preventDefault() {} } as React.FormEvent),
  });

  // Whatever stops the start (an error, the key dialog) has to be seen, not hidden under the storyboard view.
  useEffect(() => {
    if (uploadError || showTokenModal) releaseCover();
  }, [uploadError, showTokenModal]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Direction B (beta only): home is the front door's box on the studio's ground.
  const b = studioBOn();
  const pickIdea = (chip: Chip) => {
    frontDoorStart.current = { prompt: chip.prompt, chip: chip.id, look: null, landing: false };
    setMessage(chip.prompt);
  };

  return (
    <div
      className={cn(
        "w-full min-h-screen overflow-y-auto transition-all duration-500 ease-out relative isolate",
        b ? "bg-b-ground" : "studio-canvas",
        isTransitioning ? "opacity-0" : "opacity-100"
      )}
    >
      <HeroDotField />

      {/* Header. Studio B shows it only with something in it (a phone's menu and logo): the rail holds the logo and day/night. */}
      {(!b || showLogo || onToggleSidebar) && (
      <header className="sticky top-0 z-50 w-full studio-topbar">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Left side - Sidebar Toggle + Logo */}
            <div className="flex items-center gap-4">
              {/* Sidebar Toggle */}
              {onToggleSidebar && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleSidebar}
                  className="h-9 w-9 p-0 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-all"
                >
                  <Menu className="h-5 w-5 studio-text-secondary" />
                </Button>
              )}

              {/* Logo */}
              {showLogo && (
                <Link to="/" search={{ slide: undefined }} title="Home" className="flex items-center">
                  <Logo variant="modern" className="h-9" />
                </Link>
              )}
            </div>

            {/* Right side - Theme Toggle */}
            {!b && (
              <div className="flex items-center gap-2">
                <IconTipB label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleTheme}
                    className="h-9 w-9 p-0 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-all"
                    title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                  >
                    {isDarkMode ? (
                      <Sun className="h-5 w-5 studio-text-secondary" />
                    ) : (
                      <Moon className="h-5 w-5 studio-text-secondary" />
                    )}
                  </Button>
                </IconTipB>
              </div>
            )}
          </div>
        </div>
      </header>
      )}

      {/* Main Content - Scrollable */}
      <main className="relative w-full pb-20">
        {/* Hero Section */}
        <section className="relative pt-20 pb-10">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-6 max-w-4xl mx-auto">
              {/* Main Headline */}
              {b ? (
                <div className="flex flex-col items-center gap-4">
                  <IvyMark size={64} />
                  <h1 className="text-b-title text-b-t1">What should we make?</h1>
                </div>
              ) : (
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight">
                  <span className="block text-foreground">
                    Create impactful
                  </span>
                  <span className="block text-primary">
                    educational videos
                  </span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Transform knowledge into visual learning experiences with AI.
                </p>
              </div>
              )}
            </div>

            {/* Modern Input Card */}
            <div className={`mt-12 max-w-3xl mx-auto relative transition-all duration-500 ease-out ${isTransitioning
              ? "transform translate-y-96 scale-75 opacity-0"
              : "transform translate-y-0 scale-100 opacity-100"
              }`}>
              {resume.banner && hasActiveTeam && (
                <ResumeBanner draft={resume.banner} onStart={resume.onStart} onDiscard={resume.onDiscard} />
              )}

              <Card className={b ? "overflow-hidden rounded-[var(--b-radius-card)] border border-b-line bg-b-panel shadow-none" : "bg-white/70 dark:bg-black/40 backdrop-blur-3xl rounded-[24px] border border-white/40 dark:border-white/5 shadow-float overflow-hidden"}>
                <CardContent className={b ? "relative p-3 space-y-4" : "relative p-6 space-y-4"}>
                  {!hasActiveTeam ? (
                    /* No Team Selected Message */
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-amber-500/10 dark:bg-amber-500/20 rounded-2xl mx-auto mb-6 flex items-center justify-center backdrop-blur-sm">
                        <MessageSquare className="h-8 w-8 text-amber-600 dark:text-amber-500" />
                      </div>
                      <h3 className="text-2xl font-semibold studio-text-primary mb-3">
                        Team Selection Required
                      </h3>
                      <p className="studio-text-secondary mb-6 max-w-md mx-auto">
                        Please select your organization and team to begin creating
                        video content.
                      </p>
                      {onToggleSidebar && (
                        <Button
                          onClick={onToggleSidebar}
                          className="studio-ai-button font-medium px-6 py-2.5"
                        >
                          <Menu className="h-4 w-4 mr-2" />
                          Access Team Menu
                        </Button>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Message Input */}
                      <ComposerDropZone
                        onFiles={handleFilesAdded}
                        disabled={isLoading || workflowType === 'image' || kubicle1aMode}
                        className="rounded-2xl transition-all duration-200"
                      >
                        <div ref={boxRef} className={b ? "flex flex-col gap-2 p-1" : "flex flex-col gap-2 p-4 bg-white/40 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 rounded-2xl shadow-sm transition-all hover:border-primary/30 focus-within:border-primary/50"}>
                          {kubicle1aMode && (
                            <input
                              value={lessonTitle}
                              onChange={(e) => setLessonTitle(e.target.value)}
                              placeholder="Lesson title"
                              aria-label="Lesson title"
                              className="w-full px-2 py-1 bg-transparent border-b border-black/10 dark:border-white/10 focus:outline-none placeholder:text-muted-foreground text-base font-medium"
                              style={{ fontSize: "16px" }}
                            />
                          )}
                          <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={kubicle1aMode ? "Paste the lesson script. It is narrated word for word." : b ? "Describe the video: who it’s for, what they should take away…" : "Describe the video you want to create..."}
                            disabled={isLoading || kubicle1a.starting || uploadState === 'uploading' || uploadState === 'analyzing'}
                            className={b
                              ? "w-full min-h-[80px] px-2 py-2 bg-transparent resize-none focus:outline-none text-b-t1 placeholder:text-b-t3 leading-relaxed"
                              : "w-full min-h-[80px] px-2 py-2 bg-transparent resize-none focus:outline-none placeholder:text-muted-foreground text-base leading-relaxed transition-all duration-200"}
                            style={{ fontSize: "16px" }} // Prevent zoom on iOS
                          />
                          {kubicle1aMode && kubicle1a.error && (
                            <p className="px-2 text-sm text-red-600 dark:text-red-400">{kubicle1a.error}</p>
                          )}

                          {/* Staged file chips */}
                          {stagedFiles.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 px-1">
                              {stagedFiles.map((stagedFile) => (
                                <div
                                  key={stagedFile.id}
                                  className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-full text-xs"
                                >
                                  <span className="font-medium text-primary max-w-28 truncate">
                                    {stagedFile.file.name}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleFileRemoved(stagedFile.id)}
                                    className="text-primary/60 hover:text-primary transition-colors"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          <FootageChips className="px-1" />

                          {/* Reference image chip (for image workflow) */}
                          {workflowType === 'image' && referenceImage && (
                            <div className="flex items-center gap-1.5 px-1">
                              <div className="flex items-center gap-2 pl-2.5 pr-1.5 py-1 bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-full">
                                {referenceImagePreview && (
                                  <img
                                    src={referenceImagePreview}
                                    alt="Reference"
                                    className="h-5 w-5 rounded object-cover"
                                  />
                                )}
                                <span className="text-xs font-medium text-primary max-w-28 truncate">
                                  {referenceImage.name}
                                </span>
                                <button
                                  type="button"
                                  onClick={handleReferenceImageRemove}
                                  className="text-primary/60 hover:text-primary transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Bottom Action Bar */}
                          <div className="flex items-center justify-between gap-2">
                            {/* Left side - Attach + Image workflow buttons. Wraps on a phone, so Send never leaves the box. */}
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                              <AttachFilesButton onFiles={handleFilesAdded} disabled={isLoading} />

                              {/* Style picker: Kubicle 1A's look is its template library, so it has none to offer */}
                              {!kubicle1aMode && (
                              <IconTipB label={`Look: ${lookLabel}`} side="top">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Look: ${lookLabel}`}
                                  onClick={() => setShowDesignSystemModal(true)}
                                  className="h-8 px-2 gap-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors duration-200"
                                >
                                  <Palette className={cn("h-[18px] w-[18px]", selectedDesignPackId ? "text-primary" : "studio-text-secondary")} />
                                  <span className={cn("text-[11px] font-medium max-w-[150px] truncate", selectedDesignPackId ? "text-primary" : "studio-text-secondary")}>Look: {lookLabel}</span>
                                  {selectedColorPalette && (
                                    <span className="flex items-center gap-0.5 ml-0.5">
                                      {(['primary', 'secondary', 'accent'] as const).map((role) => (
                                        <span
                                          key={role}
                                          className="w-3 h-3 rounded-full border border-black/10 dark:border-white/10"
                                          style={{ backgroundColor: selectedColorPalette.roles[role] }}
                                          title={role}
                                        />
                                      ))}
                                    </span>
                                  )}
                                </Button>
                              </IconTipB>
                              )}

                              {/* Claude API key settings */}
                              <IconTipB label={tokenStatus?.hasToken ? 'Your own model key is saved' : 'Model key settings'} side="top">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowTokenModal(true)}
                                  className="h-8 w-8 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors duration-200"
                                  title={tokenStatus?.hasToken ? 'Your own model key is saved' : 'Model key settings'}
                                >
                                  <Settings className={cn("h-[18px] w-[18px]", tokenStatus?.hasToken ? "text-emerald-500" : "studio-text-secondary")} />
                                </Button>
                              </IconTipB>

                              {/* Quality tier: always in sight, Pro unless the user picks another */}
                              <TierPicker />

                              {/* Kubicle 1A: a lesson from its script, on Kubicle's staging library (Kubicle accounts only) */}
                              {kubicleAudioAllowed && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  aria-pressed={kubicle1aMode}
                                  onClick={() => setKubicle1aMode((v) => !v)}
                                  className={cn(
                                    "h-8 px-2.5 gap-1.5 rounded-full transition-colors duration-200 text-xs font-medium",
                                    kubicle1aMode
                                      ? "bg-primary/10 dark:bg-primary/20 border border-primary/30 text-primary"
                                      : "hover:bg-black/5 dark:hover:bg-white/10 studio-text-secondary"
                                  )}
                                  title={kubicle1aMode ? 'Kubicle 1A is ON: paste the lesson script, narrated word for word' : 'Start a Kubicle 1A lesson from its script'}
                                >
                                  Kubicle 1A
                                </Button>
                              )}



                              {/* Image workflow: Upload + Generate buttons */}
                              {workflowType === 'image' && !referenceImage && (
                                <>
                                  <label
                                    className="flex items-center gap-1.5 px-2.5 py-1.5 border border-dashed border-primary/30 bg-white/60 dark:bg-white/5 text-primary rounded-full hover:border-primary/50 hover:bg-primary/5 transition-colors cursor-pointer text-xs font-medium"
                                    onDrop={handleImageDrop}
                                    onDragOver={handleImageDragOver}
                                  >
                                    <Upload className="h-3.5 w-3.5" />
                                    Image
                                    <input
                                      type="file"
                                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                                      className="hidden"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleReferenceImageSelect(file);
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => setShowImageGenModal(true)}
                                    className="studio-ai-button flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all shadow-sm"
                                  >
                                    <Sparkles className="h-3.5 w-3.5" />
                                    Generate
                                  </button>
                                </>
                              )}
                            </div>

                            {/* Right side - Send button */}
                            <Button
                              type="submit"
                              size="sm"
                              disabled={
                                (workflowType !== 'image' && !message.trim()) ||
                                (workflowType === 'image' && !referenceImage) ||
                                (kubicle1aMode && !lessonTitle.trim()) ||
                                isLoading ||
                                kubicle1a.starting ||
                                uploadState === 'uploading' ||
                                uploadState === 'analyzing'
                              }
                              className={`h-8 w-8 shrink-0 self-start p-0 rounded-full transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                                tokenStatus && !tokenStatus.hasToken && !platformFunded
                                  ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                                  : b ? 'bg-b-ink text-b-on-ink hover:bg-b-ink hover:opacity-90' : 'bg-primary text-primary-foreground hover:bg-primary/90'
                              }`}
                              title={
                                tokenStatus && !tokenStatus.hasToken && !platformFunded
                                  ? 'Add your model key to start'
                                  : uploadState === 'uploading'
                                  ? 'Uploading files...'
                                  : uploadState === 'analyzing'
                                    ? 'Analyzing resources...'
                                    : workflowType === 'image' && !referenceImage
                                      ? 'Please upload an image first'
                                      : stagedFiles.length > 0
                                        ? 'Upload & Send'
                                        : 'Send'
                              }
                            >
                              {isLoading || uploadState === 'uploading' || uploadState === 'analyzing' ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </ComposerDropZone>

                      {/* Upload/Analysis progress states */}
                      {(uploadState === 'uploading' || uploadState === 'analyzing' || uploadState === 'transcribing' || uploadState === 'error') && (
                        <HeroResourceUploadArea
                          files={stagedFiles}
                          onFilesAdded={handleFilesAdded}
                          onFileRemoved={handleFileRemoved}
                          onPurposeChanged={handlePurposeChanged}
                          uploadState={uploadState}
                          uploadProgress={uploadProgress}
                          analysisJobId={analysisJobId}
                          error={uploadError}
                          onAnalysisComplete={handleAnalysisComplete}
                          uploadedFileCount={stagedFiles.length}
                          isExpanded={false}
                          onToggleExpand={() => {}}
                        />
                      )}
                    </form>
                  )}
                </CardContent>
              </Card>
              {b && hasActiveTeam && <HomeIdeasB words={message} onPick={pickIdea} />}
            </div>
          </div>
        </section>

        {/* Projects Section */}
        {(videoProjects.length > 0 || conversations.some(c => c.collectionType === "VIDEO" || c.collectionType === "SLIDE") || sharedProjects.length > 0) && (
          <section className={cn("relative py-12 min-h-[50vh]", !b && "bg-black/[0.02] dark:bg-white/[0.02]")}>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              {/* Section Header with Tabs */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h2 className={b ? "text-b-title text-b-t1" : "text-2xl sm:text-3xl font-bold studio-text-primary"}>
                    {b ? 'Your videos' : 'Your Projects'}
                  </h2>
                  <p className="text-sm studio-text-secondary mt-1">
                    {totalProjectCount} project{totalProjectCount !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-0.5 p-1 bg-muted rounded-xl">
                  <button
                    onClick={() => setProjectFilter('all')}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300",
                      projectFilter === 'all'
                        ? 'bg-background text-foreground shadow-sm'
                        : 'studio-text-secondary hover:studio-text-primary'
                    )}
                  >
                    All
                  </button>
                  {!b && (
                  <button
                    onClick={() => setProjectFilter('slide')}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5",
                      projectFilter === 'slide'
                        ? 'bg-background text-primary shadow-sm'
                        : 'studio-text-secondary hover:studio-text-primary'
                    )}
                  >
                    <Presentation className="w-4 h-4" />
                    Slides
                  </button>
                  )}
                  <button
                    onClick={() => setProjectFilter('video')}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5",
                      projectFilter === 'video'
                        ? 'bg-background text-primary shadow-sm'
                        : 'studio-text-secondary hover:studio-text-primary'
                    )}
                  >
                    <Video className="w-4 h-4" />
                    Videos
                  </button>
                  <button
                    onClick={() => setProjectFilter('shared')}
                    className={cn(
                      "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 flex items-center gap-1.5",
                      projectFilter === 'shared'
                        ? 'bg-background text-primary shadow-sm'
                        : 'studio-text-secondary hover:studio-text-primary'
                    )}
                  >
                    <Users className="w-4 h-4" />
                    Shared
                  </button>
                </div>
              </div>

              {/* Scrollable Projects Grid */}
              {projectFilter === 'shared' ? (
                /* Shared with me view */
                loadingShared ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : sharedProjects.length > 0 ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {sharedProjects.map((share) => (
                        <Card
                          key={share.id}
                          className={cn(
                            "group relative overflow-hidden transition-all duration-300",
                            "hover:border-primary/40",
                            "bg-card border-border",
                          )}
                        >
                          {/* Thumbnail */}
                          <div className="relative aspect-video bg-accent overflow-hidden flex items-center justify-center">
                            <div className="text-center p-4">
                              <Users className="w-10 h-10 text-accent-foreground mx-auto mb-2" />
                              <p className="text-xs text-accent-foreground font-medium">
                                {share.slideCount} slide{share.slideCount !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>

                          <CardContent className="p-4">
                            <div className="space-y-3">
                              <div>
                                <h3 className="font-semibold text-sm text-foreground line-clamp-2">
                                  {share.projectTitle}
                                </h3>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Shared by {share.sharedBy.displayName || share.sharedBy.email}
                                </p>
                              </div>

                              {share.permission === 'CLONE' ? (
                                <Button
                                  size="sm"
                                  className="w-full"
                                  disabled={cloningShareId === share.id}
                                  onClick={() => handleCloneShared(share)}
                                >
                                  {cloningShareId === share.id ? (
                                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5 mr-1.5" />
                                  )}
                                  {cloningShareId === share.id ? 'Cloning...' : 'Clone to My Projects'}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() => handleViewShared(share)}
                                >
                                  <Eye className="w-3.5 h-3.5 mr-1.5" />
                                  Preview
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-accent rounded-full mx-auto mb-4 flex items-center justify-center">
                      <Users className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <p className="studio-text-secondary">
                      No projects shared with you yet
                    </p>
                  </div>
                )
              ) : (loadingProjects || conversationsLoading) && videoProjects.length === 0 ? (
                // While the list loads, show skeleton cards (not a blank "no projects" state)
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="rounded-xl overflow-hidden border border-border/30 animate-pulse">
                      <div className="aspect-video bg-black/5 dark:bg-white/5" />
                      <div className="p-3 space-y-2">
                        <div className="h-3 w-3/4 rounded bg-black/5 dark:bg-white/5" />
                        <div className="h-2 w-1/3 rounded bg-black/5 dark:bg-white/5" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : videoProjects.length > 0 ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {videoProjects.map((project) => (
                      <VideoProjectCard
                        key={project.id}
                        project={project}
                        onClick={handleProjectClick}
                        onDelete={handleProjectDelete}
                        onShare={(p) => setShareDialogProject({ id: p.id, title: p.title })}
                        onClone={handleProjectClone}
                        onAnotherInLook={(ref) => {
                          setSelectedDesignPackId(ref);
                          setLookAsItWas(true);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={b ? undefined : "transform transition-all duration-300 hover:scale-[1.02]"}
                      />
                    ))}
                  </div>
                  {totalProjectCount > 12 && (
                    <div className="mt-6 text-center">
                      <Button
                        variant="outline"
                        onClick={() => navigate({ to: '/videos' })}
                        className="px-6"
                      >
                        View all {totalProjectCount} projects
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-black/5 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center backdrop-blur-sm">
                    {projectFilter === 'video' ? (
                      <Video className="w-8 h-8 text-muted-foreground" />
                    ) : (
                      <Presentation className="w-8 h-8 text-muted-foreground" />
                    )}
                  </div>
                  <p className="studio-text-secondary">
                    No {projectFilter === 'all' ? '' : projectFilter} projects yet
                  </p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Empty State - Only show when no projects exist at all (own or shared) */}
        {!loadingProjects && !loadingShared && !conversations.some(c => c.collectionType === "VIDEO" || c.collectionType === "SLIDE") && sharedProjects.length === 0 && (
          <section className="relative py-20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center space-y-6 max-w-md mx-auto">
                <div className="w-20 h-20 bg-accent rounded-full mx-auto flex items-center justify-center">
                  <Plus className="w-10 h-10 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-semibold studio-text-primary">
                    Start Your First Project
                  </h3>
                  <p className="studio-text-secondary">
                    Describe a video and we'll build it with you
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Design System Selection Modal */}
      <TemplateSelectionModal
        open={showDesignSystemModal}
        onOpenChange={setShowDesignSystemModal}
        onTemplateSelect={(_templateId, _templateName, _previewUrl, colorPalette, designPackId) => {
          setSelectedDesignPackId(designPackId);
          setLookAsItWas(false);
          setSelectedColorPalette(colorPalette);
        }}
        selectedTemplateId="blank-canvas"
        selectionMode="theme"
        designSystemOnly
      />

      {/* AI Image Generation Modal */}
      <AIImageGenerationModal
        open={showImageGenModal}
        onOpenChange={setShowImageGenModal}
        initialPrompt={message}
        onImageApproved={handleGeneratedImageApproved}
      />

      {/* Share Project Dialog */}
      {shareDialogProject && (
        <ShareProjectDialog
          open={!!shareDialogProject}
          onOpenChange={(open) => { if (!open) setShareDialogProject(null); }}
          conversationId={shareDialogProject.id}
          projectTitle={shareDialogProject.title}
        />
      )}

      {/* Claude API Key Modal */}
      <ClaudeTokenModal
        open={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          checkTokenStatus();
        }}
        qualityTier={qualityTier}
        onQualityTierChange={setQualityTier}
      />
    </div>
  );
}
